import React from 'react';
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';
import FlashRoot from 'components/FlashMessage/FlashRoot';
import DateFilter from 'components/shared/DateFilter';
import Filters from 'components/Filters/Filters';
import withFiltering from 'hocs/withFiltering';
import ContactList from 'components/ContactList/ContactList';
import withSorting from 'hocs/withSorting';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import Pagination from 'react-ultimate-pagination-bootstrap-4';
import { emailOpenRates, tags } from '../../ducks';
import { duckify, filterDuck } from '../../utils/duckify';
import './styles/EmailOpenRates.scss';
import withDateFilter from '../../hocs/withDateFilter';
import TagList from '../Tagging/TagList';
import { useTagFilter } from '../../utils/tags';
import TotalsHeader from '../shared/TotalsHeader';
import Placeholder from '../Placeholder';
import { defaultColumn, linkColumn } from '../ContactList/ColumnDefinitions';
import BulkAction from '../ContactList/BulkActions/BulkAction';
import BulkExportAction from '../ContactList/BulkActions/BulkExportAction';
import BulkActions from '../ContactList/BulkActions/BulkActions';
import { stringSortBy } from '../../utils/arrays';
import withPagination from '../../hocs/withPagination';

const percent = (count, total) => (total > 0 ? Math.round((count * 100) / total) : 0);

const withPercent = (count, total) => `${count} (${percent(count, total)}%)`;

const emailTotals = (templates) => {
  const initial = { total_count: 0, open_count: 0, click_count: 0, failed_count: 0 };
  const sums = templates.reduce(
    (accumulator, currentValue) => (
      {
        total_count: accumulator.total_count + currentValue.total_count,
        open_count: accumulator.open_count + currentValue.open_count,
        click_count: accumulator.click_count + currentValue.click_count,
        failed_count: accumulator.failed_count + currentValue.failed_count
      }),
    initial
  );
  return [
    sums.total_count,
    withPercent(sums.total_count - sums.failed_count, sums.total_count),
    withPercent(sums.open_count, sums.total_count),
    withPercent(sums.click_count, sums.total_count),
    withPercent(sums.failed_count, sums.total_count)
  ];
};

const emailHistoriesUrl = (template, currentSearch) => {
  const urlParams = new URLSearchParams(currentSearch);

  urlParams.set('filters', JSON.stringify({ snippet_id: [template.id] }));
  urlParams.delete('sort');

  return `/setup/email_histories?${urlParams.toString()}`;
};

const sortTemplates = (templates, sort) => {
  if (!sort || !sort.column) {
    return templates;
  }
  return templates.sort(stringSortBy(sort.column, sort.order));
};

const paginateTemplates = (templates, page, pageSize) => {
  const currentPage = page || 1;
  const firstIndex = (currentPage - 1) * pageSize;
  return templates.slice(firstIndex, firstIndex + pageSize);
};

const EmailOpenRates = (
  {
    ratesByTemplate,

    onSort,
    sort,

    page,
    setPage,

    filters,
    setFilters,

    dateFilter,
    tags
  }
) => {
  const columns = [
    linkColumn({
      header: 'Template Name',
      sortName: 'name',
      value: 'name',
      link: (template) => emailHistoriesUrl(template, window.location.search)
    }),
    defaultColumn({
      header: 'Subject',
      sortName: 'subject',
      value: 'subject'
    }),
    defaultColumn({
      header: 'Tags',
      value: (template) => <td><TagList tagNames={template.tag_names} /></td>
    }),
    defaultColumn({
      header: 'Total',
      value: 'total_count'
    }),
    defaultColumn({
      header: 'Sent',
      value: (template) => (
        <td className='email-open-rates-percent'>
          {withPercent(template.total_count - template.failed_count, template.total_count)}
        </td>
      )
    }),
    defaultColumn({
      header: 'Opens',
      value: (template) => (
        <td className='email-open-rates-percent'>
          {withPercent(template.open_count, template.total_count)}
        </td>
      )
    }),
    defaultColumn({
      header: 'Clicks',
      value: (template) => (
        <td className='email-open-rates-percent'>
          {template.click_count > 0 ? withPercent(template.click_count, template.total_count) : ''}
        </td>
      )
    }),
    defaultColumn({
      header: 'Failed',
      value: (template) => (
        <td className='email-opens-failed email-open-rates-percent'>
          {template.failed_count > 0 ? withPercent(template.failed_count, template.total_count) : ''}
        </td>
      )
    })
  ];

  const [unsortedTemplates, filterFields] = useTagFilter(ratesByTemplate, { filters, tags });
  const templates = sortTemplates(unsortedTemplates, sort);
  const pageSize = 50;
  const totalPages = Math.ceil(templates.length / pageSize);
  const paginatedTemplates = paginateTemplates(templates, page, pageSize);

  return (
    <div className='email-open-rates'>
      <FlashRoot />
      <h2 className='inline'>
        Email Open Rates
      </h2>

      <BulkActions>
        <BulkAction title='Export' icon={faSignOut}>
          <BulkExportAction
            type='EmailOpenRatesExportJob'
            location={location}
            url='/api/email_open_rates/export'
          />
        </BulkAction>
      </BulkActions>

      <div className='email-opens-date-filter'>
        <DateFilter
          useAll={false}
          useFutureOptions={false}
          customMessage='Searching across more than one year may be slow.'
          dateFilter={dateFilter}
        />
      </div>

      {filterFields.length !== 0 && <Filters fields={filterFields} selected={filters} submitFilters={setFilters} />}

      <div className='email-opens-failed-emails'>
        <a href='/setup/failed_emails'>View Failed Emails</a>
      </div>

      {
        ratesByTemplate.ready && ratesByTemplate.length > 0 && (
          <TotalsHeader
            columnHeaders={['Totals', 'Sent', 'Opens', 'Clicks', 'Failed']}
            columnValues={emailTotals(templates)}
          />
        )
      }

      {totalPages > 1 && (
        <div className='react-pagination'>
          <div className='pagination pagination-right'>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              hideFirstAndLastPageLinks
              onChange={setPage}
            />
          </div>
        </div>
      )}

      <Placeholder.loading
        ready={ratesByTemplate.ready}
        content={() => (
          <ContactList
            columns={columns}
            contacts={paginatedTemplates}
            emptyMessage='There are no templates.'
            onSort={onSort}
            sort={sort}
          />
        )}
      />
    </div>
  );
};

const mapDucksToProps = ({ dateFilter }) => ({
  ratesByTemplate: filterDuck(emailOpenRates, { ...dateFilter.queryParams }),
  tags
});

export default compose(
  withRouter,
  withFiltering,
  withPagination,
  withSorting({
    column: 'name',
    order: 'asc'
  }),
  withDateFilter(),
  duckify(mapDucksToProps)
)(EmailOpenRates);
